import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { debounce } from "lodash";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { getAreaTitleText } from "./EditTableButtonsDialog";
import { RED_PRIMARY, DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import {
  deriveFullAddressFromPropertyKeys,
  getPropertyId,
  sortSoldRowsBySaleDateDesc,
} from "./utils";
import MiniTableProCore from "./MiniTableProCore";
import ErrorDialog from "./ErrorDialog";
import ReviewDialog from "./ReviewDialog";

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "1170px",
    width: "99vw",
    "& .MuiDialogContent-root": {
      padding: "0 !important",
    },
  },
  dialogHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "15px 18px",
    gap: "18px",
    width: "100%",
  },
  dialogHeaderLeft: {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  dialogHeaderRight: {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  areaTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    marginBottom: "0",
    textAlign: "center",
    width: "100%",
  },
  dialogTitle: {
    fontSize: "12px",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: "0",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  dialogInstruction: {
    fontSize: "11px",
    lineHeight: "1.2",
    fontFamily: "objektiv-mk2, sans-serif",
    marginTop: "3px",
    marginBottom: "0",
  },
  dialogInstructionTitle: {
    fontSize: "11px",
    fontWeight: "600",
    lineHeight: "1.2",
    fontFamily: "objektiv-mk2, sans-serif",
    marginBottom: "3px",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "70vh",
    overflow: "hidden",
    position: "relative",
  },
  dialogBodyContent: {
    padding: "24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    minHeight: 0,
  },
  tableWrap: {
    maxWidth: "959px",
    width: "100%",
    margin: "0 auto",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: "bold",
    fontStyle: "italic",
    marginBottom: "9px",
    fontFamily: "objektiv-mk2, sans-serif",
    color: "#1f2937",
  },
  sectionInstruction: {
    fontSize: "10px",
    color: "#6b7280",
    marginBottom: "9px",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  propertiesToAddBar: {
    width: "100%",
    backgroundColor: "#f3f4f6",
    padding: "9px 12px",
    marginBottom: "12px",
    borderRadius: "4px",
    fontFamily: "objektiv-mk2, sans-serif",
    color: "#1f2937",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  propertiesToAddBarText: {
    flex: "1 1 220px",
    textAlign: "left",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  searchInputWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  searchInput: {
    width: "200px",
    height: "24px",
    minHeight: "24px",
    padding: "2px 8px",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "10px",
    fontFamily: "objektiv-mk2, sans-serif",
    lineHeight: "1.2",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "#4A7396",
    },
    "&::placeholder": {
      color: "#9ca3af",
    },
  },
  searchClearBtn: {
    padding: "4px 10px",
    border: "none",
    borderRadius: "4px",
    background: "transparent",
    color: RED_PRIMARY,
    fontSize: "12px",
    fontFamily: "objektiv-mk2, sans-serif",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  tableContainer: {
    width: "100%",
    overflow: "visible",
    border: "1px solid #e5e7eb",
    borderRadius: "4px",
    marginBottom: "12px",
    "& .regular-sales-table thead": {
      position: "sticky",
      top: "-24px",
      zIndex: 4,
      backgroundColor: "#d9f1fd",
      boxShadow: "0 1px 0 rgba(0, 0, 0, 0.08)",
    },
    "& .regular-sales-table thead th": {
      backgroundColor: "#d9f1fd !important",
      paddingTop: "5px",
      paddingLeft: "3px",
      paddingBottom: "10.5px",
    },
    // Rebalance columns in this dialog:
    // - shrink Address column
    // - give extra room to Baths and Living SqFt
    "& .regular-sales-table thead th.address-th, & .regular-sales-table tbody td.address-td": {
      width: "22% !important",
    },
    "& .regular-sales-table thead th.mini-table-baths-header, & .regular-sales-table tbody tr td:nth-last-child(2)": {
      width: "9.5% !important",
      paddingRight: "6px",
    },
    "& .regular-sales-table thead th:last-child, & .regular-sales-table tbody tr td:last-child": {
      width: "9.5% !important",
      paddingLeft: "6px",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "10px",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  tableHeader: {
    "& tr": {
      "& th": {
        fontSize: "10px",
        textAlign: "center",
        verticalAlign: "bottom",
        fontFamily: "objektiv-mk2, sans-serif",
        fontWeight: 400,
        fontStyle: "normal",
        textTransform: "uppercase",
        letterSpacing: "0.025em",
        color: "black",
        paddingTop: "2px",
        paddingBottom: "10px",
        "& p": {
          lineHeight: "11px",
          margin: 0,
        },
        "&:first-child": {
          paddingLeft: "13px",
        },
        "&:nth-child(2), &:nth-child(4), &:nth-child(6)": {
          fontWeight: 500,
        },
        "&:last-child": {
          paddingRight: "13px",
        },
      },
    },
  },
  tableBody: {
    "& tr": {
      height: "17px",
      "& td": {
        verticalAlign: "middle",
        textAlign: "center",
        height: "17px",
        fontFamily: "objektiv-mk2, sans-serif",
        fontWeight: 300,
        fontStyle: "normal",
        fontSize: "11.3px",
        color: "black",
        padding: "3.36px 2.24px",
        borderBottom: "1px solid #e5e7eb",
        // Status column: checkbox left-aligned, status text inline; keep content left-aligned for short/long status
        "&.status-text": {
          textAlign: "left",
          paddingLeft: "24px",
          "& .delete-handle-in-cell, & .reorder-handle-in-cell": {
            verticalAlign: "middle",
            flexShrink: 0,
          },
          "& p": {
            display: "inline",
            margin: 0,
            marginLeft: "6px",
            verticalAlign: "middle",
          },
        },
        // Make $ and amount inline in price columns
        "&.list-price-td, &.sale-price-td": {
          "& div": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            "& p": {
              display: "inline",
              margin: 0,
            },
          },
        },
        // Sale date on one line (month day, year)
        "&.market-table-sale-date-row": {
          "& .date-td": {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            flexWrap: "nowrap",
            whiteSpace: "nowrap",
            "& > div": {
              margin: 0,
            },
          },
        },
      },
    },
  },
  // Checkbox handle styles (component-level)
  handleInCell: {
    display: "inline-flex",
    alignItems: "center",
    verticalAlign: "middle",
    marginRight: "6px",
    flexShrink: 0,
    lineHeight: 0,
    height: "16px",
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  checkboxWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    lineHeight: 0,
    margin: 0,
    padding: 0,
  },
  checkboxInput: {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    margin: 0,
    padding: 0,
  },
  checkboxCustom: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    border: "1px solid #d1d5db",
    borderRadius: "2px",
    background: "#f9fafb",
    transition: "all 0.2s ease",
    position: "relative",
    margin: 0,
    padding: 0,
    lineHeight: 0,
    flexShrink: 0,
    cursor: "pointer",
    "&.checked": {
      background: "#4A7396",
      borderColor: "#4A7396",
      "& $checkboxX": {
        color: "white",
        fontSize: "12px",
        fontWeight: "bold",
        lineHeight: 0,
      },
    },
    "&:hover": {
      background: "#f3f4f6",
      borderColor: "#9ca3af",
      "&.checked": {
        background: "#3d5f7a",
        borderColor: "#3d5f7a",
      },
    },
  },
  checkboxX: {
    color: "white",
    fontSize: "12px",
    fontWeight: "bold",
    lineHeight: 1,
    userSelect: "none",
  },
  tableCell: {
    padding: "3.36px 2.24px",
    textAlign: "center",
    borderBottom: "1px solid #e5e7eb",
    verticalAlign: "middle",
    height: "13.6px",
    lineHeight: "13.6px",
  },
  rowSelected: {
    backgroundColor: "#ecfdf5",
  },
  rowDeleted: {
    backgroundColor: "#fef2f2",
  },
  rowAdded: {
    backgroundColor: "#eff6ff",
  },
  propertyTag: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "10px",
    fontWeight: "bold",
    marginLeft: "8px",
  },
  tagDeleted: {
    backgroundColor: RED_PRIMARY,
    color: "white",
  },
  tagAdded: {
    backgroundColor: "#3b82f6",
    color: "white",
  },
  tagAdding: {
    backgroundColor: "#10b981",
    color: "white",
  },
  manualAddSection: {
    backgroundColor: "#f3f4f6",
    textAlign: "center",
    padding: "12px 18px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    flexShrink: 0,
  },
  manualAddText: {
    fontSize: "14px",
    fontWeight: 700,
    margin: 0,
    fontFamily: "objektiv-mk2, sans-serif",
    color: "#1f2937",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 18px",
    borderTop: "1px solid #e5e7eb",
    position: "relative",
  },
  footerButtonsCenter: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  errorLink: {
    fontSize: "11px",
    color: RED_PRIMARY,
    textDecoration: "underline",
    cursor: "pointer",
    marginTop: "8px",
  },
});

/**
 * AddPropertyFromListDialog - Dialog for adding properties from available list
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Called when dialog closes
 * @param {Object} props.titleConfig - Title configuration for area name
 * @param {Array} props.currentProperties - Currently displayed properties in table
 * @param {Array} props.availableProperties - All available properties to choose from (e.g., currentSoldListings)
 * @param {Array} props.deletedProperties - Properties that were deleted (can be added back)
 * @param {Array} props.manuallyAddedProperties - Properties added manually
 * @param {number} props.maxProperties - Maximum properties allowed (default 12)
 * @param {function} props.onAddProperties - Called with array of selected property indices when UPDATE is clicked
 * @param {function} props.onManualAdd - Called when user clicks manual add button
 * @param {object} [props.currentArea] - For MiniTableProCore (same as Delete dialog)
 * @param {object} [props.quarterlyAreaTable] - For MiniTableProCore
 * @param {object} [props.agentData] - For MiniTableProCore
 */
export default function AddPropertyFromListDialog({
  open,
  onClose,
  titleConfig,
  currentProperties = [],
  availableProperties = [],
  deletedProperties = [],
  manuallyAddedProperties = [],
  maxProperties = 12,
  onAddProperties,
  onManualAdd,
  marketSaleRowProps = {},
  currentArea,
  quarterlyAreaTable,
  agentData,
  viewOnly = false,
  onViewPropertyDetails,
  mailProPropertyByListingId,
}) {
  const classes = useStyles();
  const areaTitle = getAreaTitleText(titleConfig) || "";
  
  
  const [selectedToAdd, setSelectedToAdd] = useState(new Set());
  const [selectedToAddBack, setSelectedToAddBack] = useState(new Set());
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogHeader, setErrorDialogHeader] = useState("Please note");
  const [errorDialogContent, setErrorDialogContent] = useState("");
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [propertiesToReview, setPropertiesToReview] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const tableContainerRef = useRef(null);
  const selectedOnlyForConfirmRef = useRef([]);

  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => setSearchQuery(value), 300),
    []
  );

  useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInputValue(value);
      debouncedSetSearchQuery(value);
    },
    [debouncedSetSearchQuery]
  );

  // When search query is applied, scroll the properties table into view
  useEffect(() => {
    if (searchQuery.trim()) {
      tableContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [searchQuery]);

  // Reset selections and search when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedToAdd(new Set());
      setSelectedToAddBack(new Set());
      setReviewDialogOpen(false);
      setPropertiesToReview([]);
      setSearchInputValue("");
      setSearchQuery("");
    }
  }, [open]);
  
  // Calculate allowed properties: max - current (manually added properties don't count towards limit)
  const allowedProperties = useMemo(() => {
    return Math.max(0, maxProperties - (currentProperties?.length || 0));
  }, [maxProperties, currentProperties]);
  
  // Filter out properties already in the table (by listingID)
  const availableToAdd = useMemo(() => {
    const currentIds = new Set(
      (currentProperties || []).map((p) => getPropertyId(p)).filter(Boolean)
    );
    return (availableProperties || []).filter((p) => {
      const id = getPropertyId(p);
      return id != null && !currentIds.has(id);
    });
  }, [availableProperties, currentProperties]);

  const sortedDeletedProperties = useMemo(
    () => sortSoldRowsBySaleDateDesc(deletedProperties || []),
    [deletedProperties]
  );
  const sortedManuallyAddedProperties = useMemo(
    () => sortSoldRowsBySaleDateDesc(manuallyAddedProperties || []),
    [manuallyAddedProperties]
  );
  const sortedAvailableToAdd = useMemo(
    () => sortSoldRowsBySaleDateDesc(availableToAdd || []),
    [availableToAdd]
  );

  // Use listingID as unique id
  const getPropertyIdForDialog = useCallback((property) => getPropertyId(property), []);

  /** Single list: deleted → manually added → available (order fixed) */
  const unifiedRowsAll = useMemo(() => {
    const rows = [];
    (sortedDeletedProperties || []).forEach((p) =>
      rows.push({ kind: "deleted", property: p })
    );
    (sortedManuallyAddedProperties || []).forEach((p) =>
      rows.push({ kind: "manual", property: p })
    );
    (sortedAvailableToAdd || []).forEach((p) =>
      rows.push({ kind: "available", property: p })
    );
    return rows;
  }, [sortedDeletedProperties, sortedManuallyAddedProperties, sortedAvailableToAdd]);

  const rowMatchesSearch = useCallback((property, q) => {
    if (!q) return true;
    try {
      const jsonText = JSON.stringify(property).toLowerCase();
      const fullAddress = deriveFullAddressFromPropertyKeys(property).toLowerCase();
      return `${jsonText} ${fullAddress}`.includes(q);
    } catch {
      return true;
    }
  }, []);

  const unifiedFiltered = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return unifiedRowsAll;
    return unifiedRowsAll.filter((row) => rowMatchesSearch(row.property, q));
  }, [unifiedRowsAll, searchQuery, rowMatchesSearch]);

  const selectedIndicesUnified = useMemo(() => {
    const set = new Set();
    unifiedFiltered.forEach((row, i) => {
      const id = getPropertyIdForDialog(row.property);
      if (id == null) return;
      if (row.kind === "deleted" && selectedToAddBack.has(id)) set.add(i);
      if (row.kind === "available" && selectedToAdd.has(id)) set.add(i);
    });
    return set;
  }, [unifiedFiltered, selectedToAddBack, selectedToAdd, getPropertyIdForDialog]);

  const getRowIsManuallyAdded = useCallback(
    (index) => unifiedFiltered[index]?.kind === "manual",
    [unifiedFiltered]
  );

  const getRowIsDeleted = useCallback(
    (index) => unifiedFiltered[index]?.kind === "deleted",
    [unifiedFiltered]
  );

  const handleAddCheckboxChange = useCallback((propertyId) => {
    setSelectedToAdd((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) {
        // Unchecking - always allow
        next.delete(propertyId);
        return next;
      } else {
        // Checking - check if limit exceeded
        const totalSelected = next.size + selectedToAddBack.size;
        if (totalSelected >= allowedProperties) {
          // Show error dialog, don't update selection
          setErrorDialogHeader("ERROR: Please note");
          setErrorDialogContent(
            `In order to add more than ${allowedProperties} propert${allowedProperties === 1 ? "y" : "ies"}, you will need to REMOVE, additional properties from the table.`
          );
          setErrorDialogOpen(true);
          return prev; // Don't update - return previous state
        }
        next.add(propertyId);
        return next;
      }
    });
  }, [allowedProperties, selectedToAddBack.size]);

  const handleAddBackCheckboxChange = useCallback((propertyId) => {
    setSelectedToAddBack((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) {
        // Unchecking - always allow
        next.delete(propertyId);
        return next;
      } else {
        // Checking - check if limit exceeded
        const totalSelected = selectedToAdd.size + next.size;
        if (totalSelected >= allowedProperties) {
          // Show error dialog, don't update selection
          setErrorDialogHeader("Please note");
          setErrorDialogContent(
            `In order to add more than ${allowedProperties} propert${allowedProperties === 1 ? "y" : "ies"}, you will need to REMOVE, additional properties from the table.`
          );
          setErrorDialogOpen(true);
          return prev; // Don't update - return previous state
        }
        next.add(propertyId);
        return next;
      }
    });
  }, [allowedProperties, selectedToAdd.size]);

  const handleUnifiedCheckboxChange = useCallback(
    (index) => {
      if (viewOnly) return;
      const row = unifiedFiltered[index];
      if (!row || row.kind === "manual") return;
      const id = getPropertyIdForDialog(row.property);
      if (id == null) return;
      if (row.kind === "deleted") handleAddBackCheckboxChange(id);
      else handleAddCheckboxChange(id);
    },
    [
      viewOnly,
      unifiedFiltered,
      getPropertyIdForDialog,
      handleAddBackCheckboxChange,
      handleAddCheckboxChange,
    ]
  );

  const handleUpdate = useCallback(() => {
    // Convert property IDs (listingID) back to actual properties
    const availableIds = new Map(availableToAdd.map((p) => [getPropertyId(p), p]).filter(([id]) => id != null));
    const deletedIds = new Map(deletedProperties.map((p) => [getPropertyId(p), p]).filter(([id]) => id != null));

    const toAdd = Array.from(selectedToAdd)
      .map((id) => availableIds.get(id))
      .filter(Boolean);
    const toAddBack = Array.from(selectedToAddBack)
      .map((id) => deletedIds.get(id))
      .filter(Boolean);

    const allToAdd = [...toAddBack, ...toAdd];
    if (allToAdd.length > 0) {
      selectedOnlyForConfirmRef.current = allToAdd;
      setPropertiesToReview(allToAdd);
      setReviewDialogOpen(true);
    }
  }, [selectedToAdd, selectedToAddBack, availableToAdd, deletedProperties]);

  const handleReviewConfirm = useCallback(() => {
    const selectedOnly = selectedOnlyForConfirmRef.current || [];
    if (selectedOnly.length > 0) {
      onAddProperties?.(selectedOnly);
    }
    selectedOnlyForConfirmRef.current = [];
    setSelectedToAdd(new Set());
    setSelectedToAddBack(new Set());
    setReviewDialogOpen(false);
    setPropertiesToReview([]);
    onClose?.();
  }, [onAddProperties, onClose]);

  const handleManualAdd = useCallback(() => {
    onManualAdd?.();
    // Don't close the dialog - it will be reopened after SinglePropertyAddEditDialog saves
  }, [onManualAdd]);

  const manualAddSlotsRemaining = Math.max(
    0,
    allowedProperties - (selectedToAdd.size + selectedToAddBack.size)
  );
  const manualAddButtonLabel = `Click here to MANUALLY add up to ${manualAddSlotsRemaining} ${
    manualAddSlotsRemaining === 1 ? "property" : "properties"
  }`;

  return (
    <MailProDialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
      <MailProDialogHeader style={{ minHeight: "78px" }}>
        <div className={classes.dialogHeader}>
          <div className={classes.dialogHeaderLeft}>
            {areaTitle && <div className={classes.areaTitle}>{areaTitle}</div>}
          </div>
          <div className={classes.dialogHeaderRight}>
            <div className={classes.dialogTitle}>
              {viewOnly ? "VIEW Properties" : "ADD a Property"}
            </div>
            {viewOnly ? (
              <div className={classes.dialogInstruction} style={{ marginBottom: "9px" }}>
                Showing properties that are not a part of mini table currently.
              </div>
            ) : (
              <>
                <div className={classes.dialogInstructionTitle}>
                  You have 2 options on this screen:
                </div>
                <div className={classes.dialogInstruction} style={{ marginBottom: "9px" }}>
                  <div>1. You may select properties from the table below</div>
                  <div>2. You can add a property manually</div>
                </div>
              </>
            )}
          </div>
        </div>
      </MailProDialogHeader>
      <MailProDialogBody className={classes.dialogBody}>
        <div className={classes.dialogBodyContent}>
          <div className={classes.tableWrap}>
            <div className={classes.propertiesToAddBar}>
              <span className={classes.propertiesToAddBarText}>
                {viewOnly
                  ? "Showing Deleted, Manually added and other MLS properties that you have in your PMA."
                  : `Using the list below, you may add ${allowedProperties} propert${
                      allowedProperties === 1 ? "y" : "ies"
                    } to your table`}
              </span>
              <div className={classes.searchInputWrap}>
                <input
                  type="text"
                  className={classes.searchInput}
                  placeholder="Search properties..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  aria-label="Search properties"
                />
                {searchInputValue.trim() ? (
                  <button
                    type="button"
                    className={classes.searchClearBtn}
                    onClick={() => {
                      setSearchInputValue("");
                      setSearchQuery("");
                      debouncedSetSearchQuery.cancel();
                    }}
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
            <div ref={tableContainerRef} className={classes.tableContainer}>
              {unifiedFiltered.length === 0 &&
              searchQuery.trim() &&
              unifiedRowsAll.length > 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "15px",
                    color: "#6b7280",
                    fontSize: "10px",
                    fontFamily: "objektiv-mk2, sans-serif",
                  }}
                >
                  No matching results found as per your search.
                </div>
              ) : unifiedRowsAll.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "15px",
                    color: "#6b7280",
                    fontSize: "10px",
                    fontFamily: "objektiv-mk2, sans-serif",
                  }}
                >
                  No properties to show.
                </div>
              ) : (
                <MiniTableProCore
                  miniTableProperties={unifiedFiltered.map((r) => r.property)}
                  printVersion={false}
                  addable={!viewOnly}
                  viewOnly={viewOnly}
                  selectedIndices={selectedIndicesUnified}
                  onCheckboxChange={handleUnifiedCheckboxChange}
                  getRowIsManuallyAdded={getRowIsManuallyAdded}
                  getRowIsDeleted={getRowIsDeleted}
                  hideTitleAndSubtitle={true}
                  maxRows={0}
                  currentArea={currentArea}
                  quarterlyAreaTable={quarterlyAreaTable}
                  agentData={agentData}
                  onViewPropertyDetails={onViewPropertyDetails}
                  mailProPropertyByListingId={mailProPropertyByListingId}
                />
              )}
            </div>
          </div>
        </div>
        {/* Manual add section - Fixed at bottom */}
        {!viewOnly && (
          <div className={classes.manualAddSection}>
            <div className={classes.manualAddText}>
              Don&apos;t see the property you are looking for?
            </div>
            <MailProButton
              variant="filled"
              label={manualAddButtonLabel}
              disabled={selectedToAdd.size + selectedToAddBack.size >= allowedProperties}
              onClick={handleManualAdd}
              customStyle={{
                fontSize: "14px",
                padding: "8px 16px",
                textTransform: "none",
                fontWeight: "bold",
              }}
            />
          </div>
        )}
      </MailProDialogBody>
      <div className={classes.footerButtons}>
        <MailProButton
          variant="outlined"
          label="Cancel"
          onClick={onClose}
          customStyle={{
            fontSize: "12px",
            padding: "6px 14px",
            textTransform: "uppercase",
            fontWeight: "bold",
            minWidth: "auto",
            borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
            minHeight: DIALOG_BUTTON_MIN_HEIGHT,
            height: DIALOG_BUTTON_MIN_HEIGHT,
            boxSizing: "border-box",
          }}
        />
        <div className={classes.footerButtonsCenter}>
          {!viewOnly && (
            <MailProButton
              variant="filled"
              label="Update"
              onClick={handleUpdate}
              disabled={selectedToAdd.size === 0 && selectedToAddBack.size === 0}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          )}
        </div>
        <div></div>
      </div>
      
      {/* Error Dialog */}
      {!viewOnly && (
        <ErrorDialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          headerText={errorDialogHeader}
          contentText={errorDialogContent}
        />
      )}
      
      {/* Review Dialog */}
      {!viewOnly && (
        <ReviewDialog
          open={reviewDialogOpen}
          onClose={() => {
            setReviewDialogOpen(false);
            setPropertiesToReview([]);
          }}
          areaName={areaTitle}
          properties={[...(manuallyAddedProperties || []), ...(propertiesToReview || [])]}
          onConfirm={handleReviewConfirm}
          marketSaleRowProps={marketSaleRowProps}
        />
      )}
    </MailProDialog>
  );
}
